import type { ReactNode } from 'react'
import { createContext, use, useEffect, useRef, useState } from 'react'

import type {
  PublicHelpArticle,
  PublicHelpIndex,
} from '@scrambl/shared/types/help-site'

import { fetchArticle, fetchIndex, HelpApiError } from './api'

/**
 * Data preloaded into the page: injected by the prerenderer as
 * `window.__HELP_PRELOAD__` so the first paint needs no network, and passed
 * directly to the server renderer at build time.
 */
export interface HelpPreload {
  index?: PublicHelpIndex
  articles?: Record<string, PublicHelpArticle>
  /** ISO time the preload was produced; older preloads are revalidated. */
  generatedAt?: string
}

declare global {
  interface Window {
    __HELP_PRELOAD__?: HelpPreload
  }
}

type ArticleState =
  | { status: 'loading' }
  | { status: 'ready'; article: PublicHelpArticle }
  | { status: 'missing' }
  | { status: 'error' }

interface HelpDataValue {
  index: PublicHelpIndex | null
  indexError: boolean
  articles: Record<string, ArticleState>
  ensureArticle: (id: string) => void
}

const HelpDataContext = createContext<HelpDataValue | null>(null)

const REVALIDATE_AFTER_MS = 5 * 60 * 1000

function isStale(generatedAt?: string) {
  if (!generatedAt) return true
  const age = Date.now() - new Date(generatedAt).getTime()
  return Number.isNaN(age) || age > REVALIDATE_AFTER_MS
}

export function HelpDataProvider({
  preload,
  children,
}: {
  preload?: HelpPreload
  children: ReactNode
}) {
  const [index, setIndex] = useState<PublicHelpIndex | null>(
    preload?.index ?? null,
  )
  const [indexError, setIndexError] = useState(false)
  const [articles, setArticles] = useState<Record<string, ArticleState>>(() =>
    Object.fromEntries(
      Object.entries(preload?.articles ?? {}).map(([id, article]) => [
        id,
        { status: 'ready', article },
      ]),
    ),
  )
  const inFlight = useRef(new Set<string>())
  const revalidated = useRef(new Set<string>())
  const preloadStale = isStale(preload?.generatedAt)

  useEffect(() => {
    if (index && !preloadStale) return
    let cancelled = false
    fetchIndex()
      .then(next => {
        if (!cancelled) {
          setIndex(next)
          setIndexError(false)
        }
      })
      .catch(() => {
        if (!cancelled && !index) setIndexError(true)
      })
    return () => {
      cancelled = true
    }
    // Runs once on mount: the preload decides whether a refetch is needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function ensureArticle(id: string) {
    const current = articles[id]
    const needsRevalidate =
      current?.status === 'ready' &&
      preloadStale &&
      !revalidated.current.has(id)
    if (current && !needsRevalidate) return
    if (inFlight.current.has(id)) return
    inFlight.current.add(id)
    if (!current) {
      setArticles(previous => ({ ...previous, [id]: { status: 'loading' } }))
    }
    fetchArticle(id)
      .then(article => {
        revalidated.current.add(id)
        setArticles(previous => ({
          ...previous,
          [id]: { status: 'ready', article },
        }))
      })
      .catch((error: unknown) => {
        revalidated.current.add(id)
        if (current?.status === 'ready') return
        const missing = error instanceof HelpApiError && error.status === 404
        setArticles(previous => ({
          ...previous,
          [id]: { status: missing ? 'missing' : 'error' },
        }))
      })
      .finally(() => inFlight.current.delete(id))
  }

  return (
    <HelpDataContext value={{ index, indexError, articles, ensureArticle }}>
      {children}
    </HelpDataContext>
  )
}

export function useHelpData() {
  const value = use(HelpDataContext)
  if (!value)
    throw new Error('useHelpData must be used inside HelpDataProvider')
  return value
}

export function useHelpIndex() {
  const { index, indexError } = useHelpData()
  return { index, indexError }
}

export function useArticle(id: string): ArticleState {
  const { articles, ensureArticle } = useHelpData()
  useEffect(() => {
    ensureArticle(id)
    // ensureArticle is stable enough: it closes over state via setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  return articles[id] ?? { status: 'loading' }
}
